import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';
import { S, fmtWhen } from './adminPageStyles';

/** Search scope & relevance (SRS #33): runtime overrides of the search tunables. */

const AdminSearchConfigPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const token = window.localStorage.getItem('policyora_token') ?? '';
  const [view, setView] = useState<adminApi.SearchConfigView | null>(null);
  const [sources, setSources] = useState<{ source: string; total: number }[]>([]);
  const [jurisdictions, setJurisdictions] = useState<{ name: string; code: string | null; total: number }[]>([]);
  const [form, setForm] = useState({ min_relevance: '0', semantic_enabled: false, semantic_weight: '3', semantic_min_similarity: '0.3' });
  const [excludedSources, setExcludedSources] = useState<string[]>([]);
  const [excludedJurisdictions, setExcludedJurisdictions] = useState<string[]>([]);
  const [jurFilter, setJurFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const apply = (v: adminApi.SearchConfigView) => {
    setView(v);
    setForm({ min_relevance: String(v.effective.min_relevance), semantic_enabled: v.effective.semantic_enabled,
              semantic_weight: String(v.effective.semantic_weight), semantic_min_similarity: String(v.effective.semantic_min_similarity) });
    setExcludedSources(v.effective.excluded_sources); setExcludedJurisdictions(v.effective.excluded_jurisdictions);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const [v, s] = await Promise.all([adminApi.getSearchConfig(token), adminApi.getSearchSources(token)]);
      apply(v); setSources(s.sources); setJurisdictions(s.jurisdictions);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const v = await adminApi.updateSearchConfig(token, {
        min_relevance: Number(form.min_relevance), semantic_enabled: form.semantic_enabled,
        semantic_weight: Number(form.semantic_weight), semantic_min_similarity: Number(form.semantic_min_similarity),
        excluded_sources: excludedSources, excluded_jurisdictions: excludedJurisdictions,
      });
      apply(v); setFlash('Saved — takes effect within 30 seconds on every server.'); setTimeout(() => setFlash(null), 3000);
    } catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm('Remove the override and return to the environment defaults?')) return;
    setSaving(true); setError(null);
    try { apply(await adminApi.resetSearchConfig(token)); setFlash('Reset to defaults.'); setTimeout(() => setFlash(null), 3000); }
    catch (e) { setError(e instanceof Error ? e.message : 'Reset failed'); }
    finally { setSaving(false); }
  };

  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const excludedTotal = sources.filter((s) => excludedSources.includes(s.source)).reduce((n, s) => n + s.total, 0)
    + jurisdictions.filter((j) => excludedJurisdictions.includes(j.name)).reduce((n, j) => n + j.total, 0);

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Search Config" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Search scope &amp; relevance</h2>
        <p style={S.sub}>Runtime settings for search. Environment values are the defaults; what you save here overrides them without a redeploy. Exclusions apply to everyone, on top of each account's plan.</p>
        {error && <div style={S.error}>{error}</div>}
        {flash && <div style={S.ok}>{flash}</div>}
        {view && (
          <>
            <div style={{ ...S.toolbar, marginBottom: 16 }}>
              <span style={S.chip}>{view.override ? `Override set ${fmtWhen(view.updated_at)} by ${view.updated_by || '—'}` : 'Using environment defaults'}</span>
              <span style={S.chip}>{view.embeddings.toLocaleString()} embeddings{view.semantic_possible ? '' : ' — semantic search not possible yet'}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 14, marginBottom: 18 }}>
              <label style={{ fontSize: '0.84rem', color: 'var(--ds-text-secondary)' }}>Minimum AI relevance (0 = off)
                <input style={{ ...S.input, display: 'block', width: '100%', marginTop: 4 }} type="number" min={0} max={1} step={0.05} value={form.min_relevance} onChange={(e) => setForm({ ...form, min_relevance: e.target.value })} />
                <span style={S.muted}>Default {view.defaults.min_relevance}</span>
              </label>
              <label style={{ fontSize: '0.84rem', color: 'var(--ds-text-secondary)' }}>Semantic (vector) search
                <div style={{ marginTop: 6 }}><input type="checkbox" checked={form.semantic_enabled} disabled={!view.semantic_possible} onChange={(e) => setForm({ ...form, semantic_enabled: e.target.checked })} /> enabled</div>
                <span style={S.muted}>{view.semantic_possible ? `Default ${view.defaults.semantic_enabled ? 'on' : 'off'}` : 'Needs embeddings first'}</span>
              </label>
              <label style={{ fontSize: '0.84rem', color: 'var(--ds-text-secondary)' }}>Semantic weight
                <input style={{ ...S.input, display: 'block', width: '100%', marginTop: 4 }} type="number" min={0} max={20} step={0.5} value={form.semantic_weight} onChange={(e) => setForm({ ...form, semantic_weight: e.target.value })} />
                <span style={S.muted}>Default {view.defaults.semantic_weight}</span>
              </label>
              <label style={{ fontSize: '0.84rem', color: 'var(--ds-text-secondary)' }}>Minimum similarity
                <input style={{ ...S.input, display: 'block', width: '100%', marginTop: 4 }} type="number" min={0} max={1} step={0.05} value={form.semantic_min_similarity} onChange={(e) => setForm({ ...form, semantic_min_similarity: e.target.value })} />
                <span style={S.muted}>Default {view.defaults.semantic_min_similarity}</span>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 18 }}>
              <div>
                <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Excluded sources</h3>
                <p style={{ ...S.sub, margin: '0 0 8px' }}>Tick a source system to withhold it from search entirely.</p>
                <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
                  {sources.map((s) => (
                    <label key={s.source} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid var(--ds-surface-2)', fontSize: '0.84rem', cursor: 'pointer' }}>
                      <span><input type="checkbox" checked={excludedSources.includes(s.source)} onChange={() => toggle(excludedSources, setExcludedSources, s.source)} /> <span style={S.mono}>{s.source}</span></span>
                      <span style={S.muted}>{s.total.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Excluded jurisdictions</h3>
                <input style={{ ...S.input, width: '100%', marginBottom: 8 }} placeholder="Filter jurisdictions…" value={jurFilter} onChange={(e) => setJurFilter(e.target.value)} />
                <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
                  {jurisdictions.filter((j) => !jurFilter || j.name.toLowerCase().includes(jurFilter.toLowerCase())).map((j) => (
                    <label key={`${j.name}-${j.code}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid var(--ds-surface-2)', fontSize: '0.84rem', cursor: 'pointer' }}>
                      <span><input type="checkbox" checked={excludedJurisdictions.includes(j.name)} onChange={() => toggle(excludedJurisdictions, setExcludedJurisdictions, j.name)} /> {j.name} <span style={S.muted}>{j.code}</span></span>
                      <span style={S.muted}>{j.total.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ ...S.toolbar, marginTop: 18 }}>
              <button style={S.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save override'}</button>
              <button style={S.btn} onClick={reset} disabled={saving || !view.override}>Reset to defaults</button>
              {excludedTotal > 0 && <span style={{ ...S.muted, fontSize: '0.84rem' }}>These exclusions remove {excludedTotal.toLocaleString()} regulations from search for everyone.</span>}
            </div>
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminSearchConfigPage;
